import { useTranslations } from 'next-intl';
import type { SetStateAction } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormImagePrompt, FormTabType, ImageUploadFile, UploadImageType } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';

interface UseImagePromptsOptions {
  formType: Exclude<FormTabType, 'scene' | 'character2video'>;
  maxCount?: number;
  onImagePromptsChange?: () => void;
  allowTempMode?: boolean;
}

/**
 * 管理图片列表的 Hook
 * 提供上传、删除、替换、交换图片等功能
 */
export function useImagePrompts({
  formType,
  maxCount = 10,
  onImagePromptsChange,
  allowTempMode = false,
}: UseImagePromptsOptions) {
  const t = useTranslations();
  const store = useStoreApi();
  const { imagePrompts, updateFormParams, setFormParams, userInfo } = useStore((state) => ({
    imagePrompts: state[`${formType}FormParams`].imagePrompts,
    updateFormParams: state.updateFormParams,
    setFormParams: state.setFormParams,
    userInfo: state.userInfo,
  }));
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();
  const setImagePrompts = (updater: SetStateAction<FormImagePrompt[]>) => {
    if (Array.isArray(updater)) {
      updateFormParams(formType, 'imagePrompts', updater);
    } else {
      const prevState = store.getState()[`${formType}FormParams`].imagePrompts || [];
      updateFormParams(formType, 'imagePrompts', updater(prevState as FormImagePrompt[]));
    }
  };

  // 获取有效图片数量
  const getValidImageCount = () =>
    store.getState()[`${formType}FormParams`].imagePrompts.filter((p: FormImagePrompt | null) => p !== null).length;

  // 处理图片上传
  const handleUploadImage = (uploadFile: ImageUploadFile) => {
    // 如果是开始上传，追加到数组末尾
    if (uploadFile.status === 'uploading') {
      // 如果未登录且不允许临时模式，弹出登录弹窗并拦截
      if (!userInfo && !allowTempMode) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'upload-image',
          refer_page: pathname,
        });
        return;
      }

      const validImageCount = getValidImageCount();
      if (validImageCount >= maxCount) return; // 已达到最大数量

      setImagePrompts((prev) => [
        ...(prev ?? []).filter((p) => p !== null),
        {
          id: uploadFile.id,
          status: 'uploading',
          prompt: null,
        },
      ]);
      return;
    }
    onImagePromptsChange?.();

    // 如果上传失败，移除
    if (uploadFile.status === 'error') {
      setImagePrompts((prev) => (prev ?? []).filter((p) => p?.id !== uploadFile.id));
    }

    // 上传成功，找到对应的位置更新
    if (uploadFile.status === 'done' && uploadFile.file) {
      setImagePrompts((prev) =>
        prev.map((prompt) => {
          if (prompt?.id === uploadFile.id) {
            return {
              id: uploadFile.id,
              prompt: uploadFile.file,
              status: 'done' as const,
            };
          }
          return prompt;
        }),
      );
    }
  };

  // 处理删除图片
  const handleRemoveImage = (idx: number) => {
    const deletedImagePrompt = imagePrompts[idx];

    // 如果删除的图片是 Apply 的灵感图，清空提交任务的灵感参数
    const currentFormParams = store.getState()[`${formType}FormParams`];
    const inspirationImageUrl = currentFormParams.inspirationImageUrl;
    if (deletedImagePrompt?.prompt?.sourceImage === inspirationImageUrl) {
      setFormParams(formType, {
        inspirationId: undefined,
        inspirationFrom: undefined,
        inspirationImageUrl: undefined,
      });
    }

    // 从数组中移除该图片
    setImagePrompts((prev) => (prev ?? []).filter((_, index) => index !== idx));
    onImagePromptsChange?.();
  };

  // 处理图片变化（包括替换和删除）
  const handleImageChange = (idx: number, newImage: ImageUploadFile): UploadImageType | null => {
    const targetPrompt = imagePrompts[idx];

    // 处理删除
    if (newImage.status === 'removed') {
      handleRemoveImage(idx);
      return null;
    }

    if (!targetPrompt) return null;

    if (newImage.status === 'uploading') {
      return null;
    }

    if (newImage.status === 'done' && newImage.file) {
      setImagePrompts((prev) =>
        prev.map((prompt, index) => {
          if (index === idx && prompt) {
            return {
              id: prompt.id,
              status: 'done' as const,
              prompt: newImage.file,
            };
          }
          return prompt;
        }),
      );
      onImagePromptsChange?.();
      return newImage.file;
    }

    if (newImage.status === 'error') {
      toast({
        type: 'error',
        message: t('create.createForm.upload.error.invalidImage'),
      });
    }

    return null;
  };

  // 处理交换两张图片的位置
  const handleSwapImages = (sourceIdx: number, targetIdx: number) => {
    if (
      sourceIdx < 0 ||
      sourceIdx >= imagePrompts.length ||
      targetIdx < 0 ||
      targetIdx >= imagePrompts.length ||
      sourceIdx === targetIdx
    ) {
      return;
    }

    setImagePrompts((prev) => {
      const newArray = [...prev];
      [newArray[sourceIdx], newArray[targetIdx]] = [newArray[targetIdx], newArray[sourceIdx]];
      return newArray;
    });
    onImagePromptsChange?.();
  };

  // 清空所有图片
  const clearImages = () => {
    setImagePrompts([]);
    onImagePromptsChange?.();
  };

  // 批量设置图片
  const setImages = (updater: SetStateAction<FormImagePrompt[]>) => {
    setImagePrompts(updater);
    onImagePromptsChange?.();
  };

  return {
    imagePrompts,
    validImageCount: getValidImageCount(),
    handleUploadImage,
    handleRemoveImage,
    handleImageChange,
    handleSwapImages,
    clearImages,
    setImages,
  };
}
